import React, { useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

/**
 * Searchable athlete picker. Renders like a Select but with a typeahead filter.
 *
 * Props:
 *  athletes: [{ _id, name, team_name?, belt_color?, ... }]
 *  value: athlete _id (string) or ''
 *  onChange: (id) => void
 *  placeholder?: string
 *  excludeId?: string  // hide this id from the list (avoid picking the same athlete twice)
 *  testId?: string
 */
export function AthleteCombobox({
  athletes,
  value,
  onChange,
  placeholder = 'Selecione',
  excludeId,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const selected = athletes.find((a) => a._id === value);
  const list = excludeId ? athletes.filter((a) => a._id !== excludeId) : athletes;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full flex items-center justify-between gap-2 rounded-md border border-[#27272A] bg-[#1A1A1A] px-3 py-2 text-sm text-left hover:border-[#3F3F46] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
        >
          <span className={`flex-1 truncate ${selected ? 'text-white' : 'text-[#71717A]'}`}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-[#71717A] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-[#141414] border-[#27272A] w-[var(--radix-popover-trigger-width)] max-h-[320px] overflow-hidden"
        align="start"
      >
        <Command shouldFilter className="bg-[#141414]">
          <div className="flex items-center border-b border-[#27272A] px-3">
            <Search className="w-4 h-4 text-[#71717A] mr-2 shrink-0" />
            <CommandInput
              placeholder="Buscar atleta…"
              className="flex h-10 w-full bg-transparent py-2 text-sm text-white outline-none placeholder:text-[#71717A] border-0"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <CommandList className="max-h-[260px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-[#71717A]">
              Nenhum atleta encontrado
            </CommandEmpty>
            <CommandGroup>
              {list.map((a) => (
                <CommandItem
                  key={a._id}
                  value={`${a.name} ${a.team_name || ''}`}
                  onSelect={() => {
                    onChange(a._id);
                    setOpen(false);
                  }}
                  className="text-white aria-selected:bg-[#D4AF37]/15 cursor-pointer"
                  data-testid={testId ? `${testId}-option-${a._id}` : undefined}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${a._id === value ? 'text-[#D4AF37]' : 'opacity-0'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{a.name}</div>
                    {(a.team_name || a.belt_color) && (
                      <div className="text-xs text-[#71717A] truncate">
                        {a.belt_color && <span>{a.belt_color}</span>}
                        {a.belt_color && a.team_name && <span className="mx-1">·</span>}
                        {a.team_name}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default AthleteCombobox;
